/**
 * Målindex-beräkning och härledningar i frontend – speglar
 * server/src/services/malindex/calculate/calculatePBI.ts och malindexService.
 *
 * MI = Σ(behov_i · nytta_i) / Σ behov_i, avrundat till 1 decimal, skala 0–4.
 *
 * Servern räknar och fryser indexet vid sparandet. Det här är för förhandsvisning
 * och för grafer över redan sparade besök – därför måste reglerna vara identiska.
 *
 * Bara svar inom 0–4 räknas. "Ej tillämpligt" (9) och saknat svar exkluderas ur
 * BÅDE täljare och nämnare. Noll är däremot ett giltigt svar som räknas med.
 */

import { MalindexBesok, MalindexMal, MalindexOmrade } from '../../api/malindex/malindexApi'

export type Svar = Record<string, number>

const giltigt = (v: number | undefined): v is number => v !== undefined && v >= 0 && v <= 4

/** Målindex, eller null när det inte är beräkningsbart. Visa "ej beräkningsbart", aldrig 0. */
export function calculatePBI(behov: Svar, nytta: Svar): number | null {
  const omraden = Object.keys(behov).filter((id) => giltigt(behov[id]) && giltigt(nytta[id]))
  if (omraden.length === 0) return null

  const behovSum = omraden.reduce((acc, id) => acc + behov[id], 0)
  if (behovSum === 0) return null

  const score = omraden.reduce((acc, id) => acc + behov[id] * nytta[id], 0) / behovSum
  return Math.round(score * 10) / 10
}

/** Läser ut ett svar oavsett om nyckeln är number eller string. */
export const svarForOmrade = (svar: Svar | null | undefined, omradeId: number): number | undefined => {
  if (!svar) return undefined
  return svar[omradeId] ?? svar[String(omradeId)]
}

/** Besök sorterade äldst→nyast. Muterar inte indata. */
export const kronologiskt = (besok: MalindexBesok[]): MalindexBesok[] =>
  [...besok].sort((a, b) => new Date(a.created).getTime() - new Date(b.created).getTime())

/**
 * Besöken som RÄKNAS – utkasten bortsorterade.
 *
 * Ett pausat besök producerar ingenting: inget index, ingen behovsbild som viktar
 * nästa mätning, inga mål, ingen periodgräns. Det är halva svar från ett samtal som
 * inte är slut, och de får inte påverka någonting förrän besöket sparats på riktigt.
 *
 * Ligger som ett eget steg först i varje härledning i den här filen i stället för att
 * filtreras hos anroparen. Missas det på ett ställe blir felet tyst: ett halvfärdigt
 * svar skulle vikta nästa besök utan att någon ser det i gränssnittet.
 *
 * Visningen är undantaget – tidslinjen ska visa utkastet, märkt som just utkast.
 */
export const raknade = (besok: MalindexBesok[]): MalindexBesok[] => besok.filter((b) => !b.draft)

/** Ingången till varje härledning nedan: kronologiskt OCH utan utkast. */
const underlag = (besok: MalindexBesok[]): MalindexBesok[] => kronologiskt(raknade(besok))

/**
 * Lagrade svar till flödets form. JSON har strängnycklar ({ "3": 2 }), flödena
 * indexerar på områdets id som tal. Behövs när ett utkast läses tillbaka in i flödet.
 */
export const tillFlodesSvar = (
  svar: Record<string, number> | null | undefined
): Record<number, number | undefined> => {
  const ut: Record<number, number | undefined> = {}
  Object.entries(svar ?? {}).forEach(([nyckel, varde]) => {
    ut[Number(nyckel)] = varde
  })
  return ut
}

/**
 * Den behovsskattning som viktar ett NYTT besök: den senaste som redan finns.
 * En skattning gjord under samma besök viktar först nästa – det måste gå tid
 * mellan mätningarna, annars har hemtjänsten inte hunnit agera.
 */
export function viktandeBehov(tidigareBesok: MalindexBesok[]): MalindexBesok | null {
  const medBehov = underlag(tidigareBesok).filter((b) => b.behov_svar && Object.keys(b.behov_svar).length > 0)
  return medBehov.length > 0 ? medBehov[medBehov.length - 1] : null
}

/**
 * Paret som producerade det SENASTE beräknade indexet: nyttoskattningen och den
 * behovsskattning den faktiskt vägdes mot.
 *
 * Att i stället para senaste behov med senaste nytta ger fel par i normalfallet.
 * Ett uppföljningsbesök tar ofta en ny behovsbild, och den har ännu inte viktat
 * något index – den väger nästa besök. Staplarna skulle då se ut att höra ihop
 * utan att göra det, och grafen skulle inte stämma med talet i besökslistan.
 */
export function senasteIndexPar(besok: MalindexBesok[]): { nytta: MalindexBesok; behov: MalindexBesok } | null {
  const medIndex = underlag(besok).filter((b) => b.index_total !== null && b.index_viktat_av != null)
  if (medIndex.length === 0) return null

  const nytta = medIndex[medIndex.length - 1]
  const behov = raknade(besok).find((b) => b.id === nytta.index_viktat_av)
  return behov ? { nytta, behov } : null
}

/** Senaste besöket med nyttoskattning, eller null. */
export const senasteNytta = (besok: MalindexBesok[]): MalindexBesok | null => {
  const medNytta = underlag(besok).filter((b) => b.nytta_svar && Object.keys(b.nytta_svar).length > 0)
  return medNytta.length > 0 ? medNytta[medNytta.length - 1] : null
}

/**
 * Om besöket är en kartläggning.
 *
 * Förstahandskällan är `besokstyp`, det handläggaren faktiskt valde genom att trycka
 * på en av de två knapparna i översikten. Saknas den – besöket sparades innan kolumnen
 * fanns – faller vi tillbaka på den gamla härledningen: ett besök utan nyttoskattning
 * var en kartläggning. Backfillen i malindex.sql sätter samma värde, så de två vägarna
 * ger samma svar för befintlig data.
 */
export const arKartlaggning = (b: MalindexBesok) =>
  b.besokstyp ? b.besokstyp === 'kartlaggning' : !(b.nytta_svar && Object.keys(b.nytta_svar).length > 0)

/**
 * Besöken som hör till den PÅGÅENDE perioden: från och med den senaste
 * kartläggningen och framåt.
 *
 * En kartläggning är en ny start – allt före den tillhör en tidigare omgång. Utan
 * den avgränsningen letade sig gamla mål tillbaka in i en ny period: en kartläggning
 * som medvetet sattes utan mål hoppades över, och föregående periods mål levde
 * vidare som om ingenting hänt.
 */
export function pagaendePeriod(besok: MalindexBesok[]): MalindexBesok[] {
  const sorterade = underlag(besok)
  for (let i = sorterade.length - 1; i >= 0; i -= 1) {
    if (arKartlaggning(sorterade[i])) return sorterade.slice(i)
  }
  return sorterade
}

/**
 * Målen som är aktiva just nu: de som sattes vid det senaste besöket i den pågående
 * perioden där mål sattes, minus dem som besvarats med "nej" på behåll-frågan vid
 * ett senare besök.
 *
 * Tomt när periodens kartläggning inte satte några mål – det är ett svar, inte ett
 * hål att fylla med data från förra omgången.
 */
export function aktivaMal(besok: MalindexBesok[]): MalindexMal[] {
  const period = pagaendePeriod(besok)
  const medMal = period.filter((b) => b.mal && b.mal.length > 0)
  if (medMal.length === 0) return []

  const senaste = medMal[medMal.length - 1]
  const satta = senaste.mal

  // Alla behåll-svar som avgetts EFTER att målen sattes.
  const bortvalda = new Set<string>()
  period
    .filter((b) => new Date(b.created).getTime() >= new Date(senaste.created).getTime())
    .forEach((b) =>
      (b.mal_uppfoljning ?? []).forEach((u) => {
        if (u.behall === false) bortvalda.add(u.malId)
      })
    )

  return satta.filter((m) => !bortvalda.has(m.malId))
}

/**
 * Målen som FORMULERADES vid ett besök – inte hela uppsättningen som gällde efteråt.
 *
 * `besok.mal` bär medvetet hela den gällande uppsättningen: behållna mål plus nya. Det
 * gör att det senaste besök som satte mål ensamt bär sanningen, så ingenting tappas
 * mellan besöken. Men det betyder också att `mal` INTE svarar på "vad formulerades
 * här?" – ett behållet mål ligger kvar i listan besök efter besök.
 *
 * Nytt = fanns inte i den uppsättning som gällde före besöket.
 */
export function nyaMalVid(besok: MalindexBesok, alla: MalindexBesok[]): MalindexMal[] {
  const tid = new Date(besok.created).getTime()
  const fore = underlag(alla).filter((b) => new Date(b.created).getTime() < tid)

  const senasteMedMal = [...fore].reverse().find((b) => (b.mal?.length ?? 0) > 0)
  const tidigareIds = new Set((senasteMedMal?.mal ?? []).map((m) => m.malId))

  return (besok.mal ?? []).filter((m) => !tidigareIds.has(m.malId))
}

/** En punkt i indexutvecklingen – ett värde per besök med beräknat index. */
export interface IndexPunkt {
  besokId: number
  datum: string
  index: number
}

/**
 * Id:n på de besök som bryter av mot en tidigare period – alla kartläggningar utom den
 * allra första. Den första startar visserligen också en period, men det finns ingenting
 * före den att markera en gräns mot.
 */
export function periodbrytningar(besok: MalindexBesok[]): Set<number> {
  const ut = new Set<number>()
  underlag(besok).forEach((b, i) => {
    if (i > 0 && arKartlaggning(b)) ut.add(b.id)
  })
  return ut
}

/**
 * Vilken omgång varje besök hör till, 0-baserat. En kartläggning startar nästa nummer.
 *
 * Används för att färga indexlinjen per omgång. Kartläggningen ger inget index och har
 * ingen egen punkt i grafen, så brottet hamnar mellan två punkter – färgskiftet är det
 * som gör det synligt. Talen är jämförbara över gränsen (samma skala, samma formel), så
 * linjen klipps inte av; det som behöver framgå är att behovsbilden togs om, eftersom
 * ett hopp där kan bero på nya svar snarare än på ny verklighet.
 */
export function periodNummer(besok: MalindexBesok[]): Map<number, number> {
  const ut = new Map<number, number>()
  let n = -1
  underlag(besok).forEach((b, i) => {
    if (i === 0 || arKartlaggning(b)) n += 1
    ut.set(b.id, Math.max(n, 0))
  })
  return ut
}

/** Lagrade index per besök, kronologiskt. Besök utan beräkningsbart index utelämnas. */
export const indexUtveckling = (besok: MalindexBesok[]): IndexPunkt[] =>
  underlag(besok)
    .filter((b) => b.index_total !== null && b.index_total !== undefined)
    .map((b) => ({ besokId: b.id, datum: b.created, index: b.index_total as number }))

/**
 * En rad i behov-mot-nytta-jämförelsen.
 *
 * `behov` och `nytta` är null både när svaret saknas och när det är "ej tillämpligt" –
 * ingetdera går att placera på skalan. Flaggorna skiljer dem åt, för det är två olika
 * saker för den som läser: "vi hann inte fråga" mot "frågan gäller inte den här personen".
 */
export interface BehovNyttaRad {
  omradeId: number
  namn: string
  behov: number | null
  nytta: number | null
  behovEt: boolean
  nyttaEt: boolean
}

const rent = (v: number | undefined): number | null => (giltigt(v) ? v : null)

/**
 * Alla områden ur configen, alltid – även de utan svar.
 *
 * Att sålla bort tomma rader gjorde x-axeln olika lång mellan två besök, och då går de
 * inte att jämföra med ögat. Ett område som inte gäller ska synas som en tom plats,
 * inte försvinna ur bilden.
 */
export function behovVsNytta(
  omraden: MalindexOmrade[],
  behovKalla: MalindexBesok | null | undefined,
  nyttoKalla: MalindexBesok | null | undefined,
  ejTillampligtVarde: number
): BehovNyttaRad[] {
  return omraden.map((o) => {
    const behovSvar = svarForOmrade(behovKalla?.behov_svar, o.id)
    const nyttaSvar = svarForOmrade(nyttoKalla?.nytta_svar, o.id)
    return {
      omradeId: o.id,
      namn: o.namn,
      behov: rent(behovSvar),
      nytta: rent(nyttaSvar),
      behovEt: behovSvar === ejTillampligtVarde,
      nyttaEt: nyttaSvar === ejTillampligtVarde,
    }
  })
}

/**
 * Stödord för sammanfattningen (spec 4.3, trösklarna ligger i config).
 *
 * Fungerar bra: behovet var minst `viktigtFran` OCH nyttan minst lika högt.
 * Fungerar mindre bra: behovet var minst `viktigtFran` OCH nyttan ligger minst
 * `mindreBraDiff` steg under.
 * Områden där behovet låg under `viktigtFran` nämns inte alls – de är inte
 * intressanta att prata om och drar samtalet fel.
 *
 * Returnerar stödord, inte färdiga meningar: handläggaren formulerar själv, annars
 * blir återkopplingen uppläst systemsvenska mitt i ett samtal.
 */
export interface Sammanfattning {
  fungerarBra: string[]
  fungerarMindreBra: string[]
}

export function byggSammanfattning(
  omraden: MalindexOmrade[],
  behovSvar: Svar | null | undefined,
  nyttaSvar: Svar | null | undefined,
  trosklar: { viktigtFran: number; mindreBraDiff: number }
): Sammanfattning {
  const fungerarBra: string[] = []
  const fungerarMindreBra: string[] = []

  omraden.forEach((o) => {
    const behov = rent(svarForOmrade(behovSvar, o.id))
    const nytta = rent(svarForOmrade(nyttaSvar, o.id))
    if (behov === null || nytta === null) return
    if (behov < trosklar.viktigtFran) return

    if (nytta >= behov) fungerarBra.push(o.namn)
    else if (behov - nytta >= trosklar.mindreBraDiff) fungerarMindreBra.push(o.namn)
    // Mellanläget är gråzon och lyfts inte – handläggaren kan förstås ta upp det ändå.
  })

  return { fungerarBra, fungerarMindreBra }
}

/** En rad i "nu mot förra gången"-jämförelsen som visas EFTER behovsfrågorna. */
export interface BehovJamforelseRad {
  omradeId: number
  namn: string
  nu: number | null
  forra: number | null
  forandring: number | null
}

/**
 * Jämför de nyss angivna behovssvaren mot förra behovsskattningen.
 *
 * Visas medvetet FÖRST EFTER att frågorna besvarats. Att visa förra gångens svar
 * innan ankrar brukaren – spec 2.8: "förra gången sa du att trygghet var mycket
 * viktigt, stämmer det?" besvaras med ja av artighet snarare än av sanning.
 */
export function behovJamforelse(
  omraden: MalindexOmrade[],
  nuSvar: Svar,
  forraBesok: MalindexBesok | null | undefined
): BehovJamforelseRad[] {
  return omraden.map((o) => {
    const nu = rent(svarForOmrade(nuSvar, o.id))
    const forra = rent(svarForOmrade(forraBesok?.behov_svar, o.id))
    return {
      omradeId: o.id,
      namn: o.namn,
      nu,
      forra,
      forandring: nu !== null && forra !== null ? nu - forra : null,
    }
  })
}
